/**
 * City quick links component for services menu
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { SWISS_CITIES, type ServiceId } from '@/config/servicesMenu';
import { trackCityQuickLink } from '@/analytics/menu';
import type { SwissLanguage } from '@/utils/languageTypes';

interface CityQuickLinksProps {
  serviceId: ServiceId;
  servicePathFactory: (locale: SwissLanguage) => string;
  onCityClick?: () => void;
}

const CityQuickLinks: React.FC<CityQuickLinksProps> = ({ 
  serviceId, 
  servicePathFactory, 
  onCityClick 
}) => {
  const { currentLanguage } = useLanguage();
  
  const handleCityClick = (citySlug: string) => {
    trackCityQuickLink({
      service_id: serviceId,
      city: citySlug,
      locale: currentLanguage as SwissLanguage
    });
    
    if (onCityClick) {
      onCityClick();
    }
  };

  const translations = {
    de: { title: 'Städte' },
    en: { title: 'Cities' },
    fr: { title: 'Villes' },
    it: { title: 'Città' }
  };

  const t = translations[currentLanguage as SwissLanguage];

  return (
    <div className="mt-4 pt-4 border-t border-border/50">
      <div className="flex items-center mb-3">
        <MapPin className="w-4 h-4 text-muted-foreground mr-2" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {t.title}
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        {SWISS_CITIES.slice(0, 8).map(city => {
          const basePath = servicePathFactory(currentLanguage as SwissLanguage);
          const cityPath = `${basePath}${city.slug}/`;
          
          return (
            <Link
              key={city.id}
              to={cityPath}
              onClick={() => handleCityClick(city.slug)}
              className="group flex items-center px-3 py-2 text-sm text-muted-foreground hover:text-primary hover:bg-accent/50 rounded-md transition-colors"
            >
              <span className="text-xs mr-2 opacity-60">•</span>
              <span className="truncate">
                {city.name[currentLanguage as SwissLanguage]}
              </span>
            </Link>
          );
        })}
      </div>
      
      {/* International cities teaser */}
      <div className="mt-3 pt-3 border-t border-border/30">
        <Link
          to={`/${currentLanguage}/international/`}
          className="flex items-center text-xs text-primary hover:text-primary/80 transition-colors"
        >
          <span className="mr-2">🌍</span>
          <span>
            {currentLanguage === 'de' ? 'International: Stuttgart, München, Paris, Milano...' :
             currentLanguage === 'fr' ? 'International: Stuttgart, Munich, Paris, Milan...' :
             currentLanguage === 'it' ? 'Internazionale: Stuttgart, Monaco, Parigi, Milano...' :
             'International: Stuttgart, Munich, Paris, Milan...'}
          </span>
        </Link>
      </div>
    </div>
  );
};

export default CityQuickLinks;